/*
 * File: OrderDetails.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Displays the details page for an order
 */
import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import UIPatientLink from "components/ui/UIPatientLink";
import UIListItem from "components/ui/UIListItem";
import { Typography, List } from "@material-ui/core";
import OrderStatusIndicator from "./OrderStatusIndicator";
import OrderReferences from "../store/OrderReferences";
import FabSpacer from "../../../components/ui/FabSpacer";
import OrderNotesPanels from "../components/OrderNotesPanels";
import MainHeader from "components/MainHeader";
import { Toolbar } from "@material-ui/core";
import { StoreContext } from "utils/useStore";
import { observer } from "mobx-react";

const styles = theme => ({
  toolbar: {
    padding: theme.spacing(2.5),
    display: "block",
    marginBottom: theme.spacing(-1),
    marginTop: theme.spacing(2.5)
  }
});

const OrderDetails = ({ classes, order }) => {
  const storeManager = useContext(StoreContext);

  useEffect(() => {
    // Ensure the incidents have been loaded for this patient.
    // MobX should handle rerendering if the incidents change.
    (async () => {
      await order.populateIncidents(storeManager, order.patientId);
    })();
  }, [order, storeManager]);

  const shortDescriptionGroup = {
    label: "Short Description",
    value: order.shortDescription
  };

  const longDescriptionGroup = {
    label: "Long Description",
    value: order.longDescription
  };

  const patientLink = () => {
    return <UIPatientLink id={order.patientId} name={order.patientName} />;
  };

  const orderStatusIndicator = () => {
    const openStatusIds = storeManager
      .getStore(OrderReferences)
      .getOpenStatusIds();
    if (openStatusIds.includes(order.orderStatusId))
      return <OrderStatusIndicator statusId={order.orderStatusId} />;
  };

  return (
    <React.Fragment>
      <Toolbar className={classes.toolbar}>
        <MainHeader
          pageTitle={order.typeString(storeManager)}
          pageSubtitle="Orders"
        />
        <Typography variant="h6">
          {order.statusString(storeManager)} {orderStatusIndicator()}
        </Typography>
      </Toolbar>

      <List>
        <UIListItem label="Patient" value={patientLink()} />
        <UIListItem label="Order Type" value={order.typeString(storeManager)} />
        <UIListItem label="Order Number" value={order.ordersId} />
        <UIListItem labelGroup={shortDescriptionGroup} />
        <UIListItem preserveValueWhiteSpace labelGroup={longDescriptionGroup} />
        <UIListItem label="Due Date" value={order.dueDate} />
        <UIListItem label="Assigned To" value={order.assignedToName} />
        <UIListItem label="Provider" value={order.providerName} />
        <UIListItem label="Office" value={order.officeString(storeManager)} />
        <UIListItem label="Incident" value={order.incident(storeManager)} />
        <OrderNotesPanels order={order} />
        <FabSpacer />
      </List>
    </React.Fragment>
  );
};

OrderDetails.propTypes = {
  order: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(observer(OrderDetails));
